import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Star, X, MessageSquarePlus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const FEEDBACK_UNTIL = process.env.REACT_APP_FEEDBACK_UNTIL;

const EXPERIENCE_TAGS = [
  "Easy to use",
  "Looks great",
  "Helpful recommendations",
  "Would recommend",
  "Confusing navigation",
  "Irrelevant results",
  "Too slow",
  "Missing info I needed",
];

const isExpired = () => {
  if (!FEEDBACK_UNTIL) return false;
  const end = new Date(`${FEEDBACK_UNTIL}T23:59:59`);
  return Number.isNaN(end.getTime()) ? false : new Date() > end;
};

export function ExperienceFeedbackWidget({ userMessageCount = 0, visitId, sessionId }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(() => localStorage.getItem("cml_exp_feedback_done") === "true");
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const hidden = done || isExpired();

  // Auto-open once per user after meaningful engagement (2+ messages or ~90s on page)
  useEffect(() => {
    if (hidden) return undefined;
    const alreadyPrompted = () => localStorage.getItem("cml_exp_feedback_prompted") === "true";
    const prompt = () => {
      if (alreadyPrompted()) return;
      localStorage.setItem("cml_exp_feedback_prompted", "true");
      setOpen(true);
    };
    if (userMessageCount >= 2) {
      prompt();
      return undefined;
    }
    const t = setTimeout(prompt, 90000);
    return () => clearTimeout(t);
  }, [userMessageCount, hidden]);

  const toggleTag = (tag) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const submit = async () => {
    if (!stars) {
      toast.error("Please pick a star rating first");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/experience-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stars, comment, tags, visit_id: visitId, session_id: sessionId }),
      });
      if (!res.ok) throw new Error("failed");
      localStorage.setItem("cml_exp_feedback_done", "true");
      setDone(true);
      toast.success("Thank you — your feedback helps us improve!");
    } catch {
      toast.error("Could not send feedback. Please try again.");
    }
    setSubmitting(false);
  };

  if (hidden) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[150] flex flex-col items-end" data-testid="experience-feedback-widget">
      {open && (
        <div
          className="mb-3 w-[min(92vw,22rem)] bg-[#f5f0e8] border border-[#e8e0d0] rounded-2xl shadow-2xl overflow-hidden animate-in"
          data-testid="experience-feedback-card"
        >
          <div className="bg-[#1e3a5f] px-4 py-3 flex items-start justify-between gap-2">
            <div>
              <p className="text-[#f5f0e6] font-semibold text-sm">How's your experience?</p>
              <p className="text-[#c9a962] text-xs">Quick feedback on the site overall</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[#f5f0e6]/70 hover:text-[#f5f0e6] p-1 rounded-md hover:bg-white/10"
              data-testid="experience-feedback-close-btn"
              aria-label="Close feedback"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex items-center gap-1" onMouseLeave={() => setHovered(0)}>
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => setStars(s)}
                  onMouseEnter={() => setHovered(s)}
                  className="p-0.5 transition-transform hover:scale-110"
                  data-testid={`experience-star-${s}`}
                  aria-label={`${s} star`}
                >
                  <Star
                    className={`w-7 h-7 transition-colors ${
                      (hovered || stars) >= s ? "fill-[#c9a962] text-[#c9a962]" : "text-[#c9bfa8]"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {EXPERIENCE_TAGS.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    tags.includes(tag)
                      ? "bg-[#1e3a5f] text-[#f5f0e6] border-[#1e3a5f]"
                      : "bg-white text-[#1e3a5f] border-[#e8e0d0] hover:border-[#c9a962]"
                  }`}
                  data-testid={`experience-tag-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Anything else we should know? (optional)"
              className="w-full text-sm rounded-lg border border-[#e8e0d0] bg-white px-3 py-2 text-[#1e3a5f] placeholder:text-[#1e3a5f]/40 focus:outline-none focus:border-[#1e3a5f] resize-none"
              data-testid="experience-feedback-comment"
            />

            <button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-[#1e3a5f] text-[#f5f0e6] py-2.5 rounded-xl text-sm font-semibold hover:bg-[#2a4d6e] disabled:opacity-50 transition-colors"
              data-testid="experience-feedback-submit-btn"
            >
              {submitting ? "Sending..." : "Send feedback"}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-[#c9a962] text-[#1e3a5f] text-sm font-semibold shadow-lg hover:bg-[#d8bd7e] hover:shadow-xl transition-all"
        data-testid="experience-feedback-toggle-btn"
      >
        <MessageSquarePlus className="w-4 h-4" />
        {open ? "Hide" : "Feedback"}
      </button>
    </div>
  );
}
